// src/api/constants/status.ts
// Consolidated status visual configuration — PRD Section 9 (FR-001).
// All 9 task statuses with icon, hex colors, Tailwind classes, and label.

export interface StatusEntry {
  icon: string;
  fg: string;
  bg: string;
  tailwindFg: string;
  tailwindBg: string;
  label: string;
}

/**
 * Visual configuration for all 9 task statuses.
 * Values are exact matches to PRD Section 9.
 */
export const STATUS_CONFIG: Record<string, StatusEntry> = {
  pending: {
    icon: "\u23F3",
    fg: "#6b7280",
    bg: "#f3f4f6",
    tailwindFg: "text-gray-500",
    tailwindBg: "bg-gray-100",
    label: "Pending",
  },
  ready: {
    icon: "\uD83D\uDFE2",
    fg: "#10b981",
    bg: "#d1fae5",
    tailwindFg: "text-emerald-500",
    tailwindBg: "bg-emerald-100",
    label: "Ready",
  },
  in_progress: {
    icon: "\uD83D\uDD35",
    fg: "#3b82f6",
    bg: "#dbeafe",
    tailwindFg: "text-blue-500",
    tailwindBg: "bg-blue-100",
    label: "In Progress",
  },
  complete: {
    icon: "\u2705",
    fg: "#22c55e",
    bg: "#dcfce7",
    tailwindFg: "text-green-500",
    tailwindBg: "bg-green-100",
    label: "Complete",
  },
  failed: {
    icon: "\u274C",
    fg: "#ef4444",
    bg: "#fee2e2",
    tailwindFg: "text-red-500",
    tailwindBg: "bg-red-100",
    label: "Failed",
  },
  blocked: {
    icon: "\uD83D\uDEAB",
    fg: "#f59e0b",
    bg: "#fef3c7",
    tailwindFg: "text-amber-500",
    tailwindBg: "bg-amber-100",
    label: "Blocked",
  },
  skipped: {
    icon: "\u23ED",
    fg: "#9ca3af",
    bg: "#f9fafb",
    tailwindFg: "text-gray-400",
    tailwindBg: "bg-gray-50",
    label: "Skipped",
  },
  cancelled: {
    icon: "\uD83D\uDEAE",
    fg: "#6b7280",
    bg: "#f3f4f6",
    tailwindFg: "text-gray-500",
    tailwindBg: "bg-gray-100",
    label: "Cancelled",
  },
  needs_human: {
    icon: "\uD83D\uDC64",
    fg: "#8b5cf6",
    bg: "#ede9fe",
    tailwindFg: "text-violet-500",
    tailwindBg: "bg-violet-100",
    label: "Needs Human",
  },
};

/**
 * Convenience map: status key -> label string.
 */
export const STATUS_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(STATUS_CONFIG).map(([key, entry]) => [key, entry.label]),
);

/**
 * Phase names for the 6 execution phases (0-5).
 * Values from PRD Section 9.
 */
export const PHASE_NAMES: Record<number, string> = {
  0: "Environment Setup",
  1: "Foundation",
  2: "Core Implementation",
  3: "Integration",
  4: "Verification",
  5: "Deployment",
};

/**
 * Hue values for phase color coding (used in HSL color generation).
 * Each phase gets a distinct hue for visual differentiation.
 */
export const PHASE_HUE: Record<number, number> = {
  0: 210, // blue — setup
  1: 270, // purple — foundation
  2: 150, // green — core
  3: 30, // orange — integration
  4: 60, // yellow — verification
  5: 330, // pink — deployment
};

// ---------------------------------------------------------------------------
// Dark-theme dashboard visual configs (ListView, GraphView, StatusHeader)
// ---------------------------------------------------------------------------

/**
 * Text-symbol status icons used in the dark-theme dashboard.
 * Unlike STATUS_CONFIG.icon (emoji), these are minimal text glyphs.
 */
export const STATUS_ICON: Record<string, string> = {
  pending:     "\u25CB",  // ○
  complete:    "\u2713",  // ✓
  deferred:    "\u23ED",  // ⏭
  setup:       "\u2699",  // ⚙
  waiting:     "\u23F3",  // ⏳
  in_progress: "\u25CF",  // ●
  failed:      "\u2717",  // ✗
  needs_human: "\u270B",  // ✋
  skipped:     "\u2014",  // —
  ready:       "\u25B6",  // ▶
};

/**
 * Tailwind text-color classes per status (ListView).
 */
export const STATUS_TEXT_COLOR: Record<string, string> = {
  complete:    "text-green-400",
  deferred:    "text-cyan-400",
  setup:       "text-purple-400",
  waiting:     "text-amber-400",
  in_progress: "text-blue-400",
  failed:      "text-red-400",
  needs_human: "text-amber-400",
  skipped:     "text-zinc-600",
  pending:     "text-zinc-500",
};

/**
 * Tailwind bg + border classes per status badge (ListView).
 */
export const STATUS_BADGE_BG: Record<string, string> = {
  complete:    "bg-green-500/10 border-green-500/20",
  deferred:    "bg-cyan-500/10 border-cyan-500/20",
  setup:       "bg-purple-500/10 border-purple-500/20",
  waiting:     "bg-amber-500/10 border-amber-500/20",
  in_progress: "bg-blue-500/10 border-blue-500/20",
  failed:      "bg-red-500/10 border-red-500/20",
  needs_human: "bg-amber-500/10 border-amber-500/20",
  skipped:     "bg-zinc-800 border-zinc-700",
  pending:     "bg-zinc-900 border-zinc-800",
};

/**
 * Hex-color graph node configuration per status (GraphView).
 * Each entry has bg (fill), fg (text), border (stroke), and label.
 */
export const GRAPH_STATUS_CFG: Record<string, { bg: string; fg: string; border: string; label: string }> = {
  complete:    { bg: "#052e16", fg: "#4ade80", border: "#166534", label: "DONE" },
  deferred:    { bg: "#083344", fg: "#22d3ee", border: "#155e75", label: "DEFER" },
  setup:       { bg: "#2e1065", fg: "#c084fc", border: "#581c87", label: "SETUP" },
  waiting:     { bg: "#422006", fg: "#fbbf24", border: "#854d0e", label: "WAITING" },
  in_progress: { bg: "#172554", fg: "#60a5fa", border: "#1e40af", label: "RUNNING" },
  failed:      { bg: "#450a0a", fg: "#f87171", border: "#991b1b", label: "FAILED" },
  needs_human: { bg: "#431407", fg: "#fb923c", border: "#9a3412", label: "HUMAN" },
  pending:     { bg: "#18181b", fg: "#71717a", border: "#27272a", label: "PENDING" },
  ready:       { bg: "#1e1b4b", fg: "#facc15", border: "#854d0e", label: "READY" },
  skipped:     { bg: "#18181b", fg: "#3f3f46", border: "#27272a", label: "SKIP" },
};

/**
 * Phase display names used in GraphView (compact uppercase labels).
 * Distinct from PHASE_NAMES which uses longer form names.
 */
export const GRAPH_PHASE_NAMES: Record<number, string> = {
  0: "INIT", 1: "EXPLORE", 2: "PLAN",
  3: "CODE", 4: "VERIFY", 5: "SHIP",
};

/**
 * Phase hex colors for GraphView tooltip display.
 */
export const GRAPH_PHASE_COLOR: Record<number, string> = {
  0: "#c4b5fd", 1: "#93c5fd", 2: "#6ee7b7",
  3: "#fcd34d", 4: "#f9a8d4", 5: "#fca5a5",
};

/**
 * Status flow node definitions for the StatusHeader diagram.
 * Indices: 0=start 1=pending 2=setup 3=ready 4=waiting 5=running
 *          6=done 7=failed 8=human 9=deferred 10=skipped 11=end
 */
export interface FlowNodeDef {
  key: string;
  icon: string;
  label: string;
  color: string;
  bg: string;
}

export const FLOW_NODES: FlowNodeDef[] = [
  { key: "_start",      icon: "\u25C9", label: "START",    color: "#a1a1aa", bg: "#27272a" },
  { key: "pending",     icon: "\u25CB", label: "PENDING",  color: "#71717a", bg: "#18181b" },
  { key: "setup",       icon: "\u2699", label: "SETUP",    color: "#c084fc", bg: "#2e1065" },
  { key: "ready",       icon: "\u25B6", label: "READY",    color: "#facc15", bg: "#1e1b4b" },
  { key: "waiting",     icon: "\u23F3", label: "WAITING",  color: "#fbbf24", bg: "#422006" },
  { key: "in_progress", icon: "\u25CF", label: "RUNNING",  color: "#60a5fa", bg: "#172554" },
  { key: "complete",    icon: "\u2713", label: "DONE",     color: "#4ade80", bg: "#052e16" },
  { key: "failed",      icon: "\u2717", label: "FAILED",   color: "#f87171", bg: "#450a0a" },
  { key: "needs_human", icon: "\u270B", label: "HUMAN",    color: "#fb923c", bg: "#431407" },
  { key: "deferred",    icon: "\u23ED", label: "DEFERRED", color: "#22d3ee", bg: "#083344" },
  { key: "skipped",     icon: "\u2014", label: "SKIPPED",  color: "#a1a1aa", bg: "#18181b" },
  { key: "_end",        icon: "\u25C9", label: "END",      color: "#a1a1aa", bg: "#27272a" },
];
